import axios from 'axios'
import { io } from 'socket.io-client'
import { buildApiUrl, getApiBase } from '@/config/api.js'

console.log('🔧 Super端API初始化 - 专用于Server端对接')

function getApiConfig() {
  const serverUrl = getApiBase()
  const timeout = window.APP_CONFIG?.TIMEOUT || 30000
  console.log('✅ [Super API] 当前 API 根地址:', serverUrl)
  return { serverUrl, timeout }
}

const API_CONFIG = getApiConfig()

const apiClient = axios.create({
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 🎯 添加请求拦截器，自动添加认证头
apiClient.interceptors.request.use(
  config => {
    // 统一转为绝对 URL，防止相对 /api 打到 a.xingyiai.com
    if (config.url && !/^https?:\/\//i.test(config.url)) {
      config.url = buildApiUrl(config.url)
    }
    config.baseURL = undefined

    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 如果是FormData，删除Content-Type让浏览器自动设置
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  error => Promise.reject(error)
)

function getAuthToken() {
  return localStorage.getItem('super_admin_token') || localStorage.getItem('admin_token')
}

async function serverApiCall(endpoint, method = 'GET', data = null, options = {}) {
  try {
    const config = {
      method,
      url: endpoint,
      timeout: options.timeout || API_CONFIG.timeout, // 支持自定义超时
      ...options
    }
    
    // 设置默认头信息（超管后台不需要租户概念）
    config.headers = {
      'Content-Type': 'application/json',
      // 移除 X-Merchant-Id：超级管理员后台不需要租户概念
      ...config.headers
    }
    
    const token = getAuthToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 如果是FormData，移除Content-Type让浏览器自动设置
    if (data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE')) {
      config.data = data
    } else if (data && method === 'GET') {
      config.params = data
    }
    
    const response = await apiClient(config)
    
    // 🔥 统一数据格式转换 - 解决前后端数据结构不匹配
    let responseData = response.data
    
    // 特殊处理兑换码数据格式
    if (endpoint.includes('/shop-codes/list')) {
      return {
        success: response.data.success !== false,
        data: response.data.data, // 保持完整的分页结构
        message: response.data.message,
        ...response.data
      }
    }
    
    // 特殊处理工具列表格式
    if (endpoint.includes('/shop/tools') && response.data.data && response.data.data.tools) {
      return {
        success: response.data.success !== false,
        data: response.data.data.tools, // 直接返回工具数组
        pagination: response.data.data.pagination,
        message: response.data.message,
        ...response.data
      }
    }

    // Key 池列表 GET /api/super/key-pools：保持 { list, pagination }，供表格使用
    if (
      method === 'GET' &&
      endpoint === '/api/super/key-pools' &&
      response.data?.data &&
      Array.isArray(response.data.data.list)
    ) {
      const inner = response.data.data
      return {
        success: response.data.success !== false,
        data: inner,
        message: response.data.message,
        pagination: inner.pagination,
        ...response.data
      }
    }

    // Key 池：按插件 ID 拉取（纯数组，含 status / errorCount / lastErrorMsg 等）
    // 须先于下方通用 data 解包，避免与其它接口嵌套结构混淆导致字段丢失
    if (
      method === 'GET' &&
      endpoint.startsWith('/api/super/key-pools/plugin/') &&
      response.data?.success !== false &&
      Array.isArray(response.data.data)
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      }
    }
    
    // 如果有嵌套的data字段，提取它
    if (responseData.data) {
      responseData = responseData.data
    }
    
    // 如果是分页数据格式，提取实际的数据数组
    if (responseData && typeof responseData === 'object' && !Array.isArray(responseData)) {
      // 检查常见的数据字段名
      if (responseData.tools) responseData = responseData.tools
      else if (responseData.tenants) responseData = responseData.tenants  
      else if (responseData.categories) {
        responseData = responseData.categories
      }
      else if (responseData.orders) responseData = responseData.orders
      else if (responseData.users) responseData = responseData.users
    }
    
    // 商城分类数据格式转换：id -> _id (前端兼容)
    if (endpoint.includes('/api/shop/categories') && Array.isArray(responseData)) {
      responseData = responseData.map(cat => {
        const newCat = {
          ...cat,
          _id: cat.id || cat._id,
          id: cat.id || cat._id
        }
        // 处理子分类
        if (Array.isArray(newCat.subcategories)) {
          newCat.subcategories = newCat.subcategories.map(sub => ({
            ...sub,
            _id: sub.id || sub._id,
            id: sub.id || sub._id
          }))
        }
        return newCat
      })
    }
    
    return {
      success: response.data.success !== false,
      data: responseData,
      message: response.data.message,
      pagination: response.data.data?.pagination, // 保留分页信息
      ...response.data
    }
    
  } catch (error) {
    console.error(`❌ Server API调用失败 [${method} ${endpoint}]:`, error)
    
    if (error.response) {
      return {
        success: false,
        message: error.response.data?.message || error.message,
        error: error.response.data,
        status: error.response.status
      }
    } else {
      return {
        success: false,
        message: '网络连接失败，请检查服务器状态',
        error: '网络错误'
      }
    }
  }
}

export const adminAPI = {
  // 认证相关
  adminLogin: (data) => serverApiCall('/api/auth/admin/login', 'POST', data),
  superAdminLogin: (data) => serverApiCall('/api/auth/admin/login', 'POST', data),
  validateAdminToken: (data) => serverApiCall('/api/auth/validate', 'POST', data),
  
  // 用户管理
  getUsers: (data) => serverApiCall('/api/users', 'GET', data),
  updateUser: (data) => serverApiCall(`/api/users/${data.id}`, 'PUT', data),
  updateUserPoints: (data) => serverApiCall('/api/users/points', 'POST', data),
  
  // 租户管理
  getTenants: (data) => serverApiCall('/api/tenants', 'GET', data),
  createTenant: (data) => serverApiCall('/api/tenants', 'POST', data),
  updateTenant: (data) => serverApiCall(`/api/tenants/${data.id}`, 'PUT', data),
  deleteTenant: (data) => serverApiCall(`/api/tenants/${data.id}`, 'DELETE'),
  
  // 工具管理
  getTools: (data) => {
    // 参数转换：limit -> size，确保获取所有数据
    const params = {
      ...data,
      size: data.limit || data.size || 100, // 默认获取100条，确保获取所有数据
      status: data.status === undefined ? '' : data.status // 空字符串表示获取所有状态
    };
    // 移除limit参数避免冲突
    delete params.limit;
    return serverApiCall('/api/shop/tools', 'GET', params);
  },
  createTool: (data) => serverApiCall('/api/shop/tools', 'POST', data),
  updateTool: (data) => {
    // 确保有ID
    if (!data.id && !data._id) {
      console.error('❌ 更新工具失败: 缺少ID字段', data);
      return Promise.resolve({ 
        success: false, 
        message: '更新失败：工具ID不存在' 
      });
    }
    
    const toolId = data.id || data._id;
    const apiPath = `/api/shop/tools/${toolId}`;
    
    console.log(`🔄 更新商城工具 ID: ${toolId}`, {
      apiPath,
      dataKeys: Object.keys(data),
      idFields: { 
        id: data.id, 
        _id: data._id,
        toolDataId: data.toolData?.id,
        toolData_Id: data.toolData?._id
      }
    });
    
    return serverApiCall(apiPath, 'PUT', data);
  },
  deleteTool: (data) => {
    const toolId = data.id || data._id;
    if (!toolId) {
      console.error('❌ 删除工具失败: 缺少ID字段');
      return Promise.resolve({ 
        success: false, 
        message: '删除失败：工具ID不存在' 
      });
    }
    const url = data.force 
      ? `/api/shop/tools/${toolId}?force=true`
      : `/api/shop/tools/${toolId}`;
    return serverApiCall(url, 'DELETE');
  },
  
  // 分类管理
  getCategories: () => serverApiCall('/api/shop/categories', 'GET'),
  updateCategories: (data) => serverApiCall('/api/shop/categories', 'PUT', data),
  deleteCategory: (data) => serverApiCall(`/api/shop/categories/${data.id || data._id}`, 'DELETE'),
  clearCategories: () => serverApiCall('/api/shop/categories/clear', 'POST'),
  
  // 订单管理
  getOrders: (data) => serverApiCall('/api/shop/orders', 'GET', data),
  updateOrder: (data) => serverApiCall(`/api/shop/orders/${data.id}`, 'PUT', data),
  deleteOrder: (data) => serverApiCall(`/api/shop/orders/${data.id}`, 'DELETE'),
  getToolOrders: (data) => serverApiCall('/api/shop/orders', 'GET', data),
  getToolOrderStatistics: () => serverApiCall('/api/shop/orders/statistics', 'GET'),
  
  // 消息管理
  getMessages: (data) => serverApiCall('/api/messages', 'GET', data),
  sendMessage: (data) => serverApiCall('/api/messages', 'POST', data),
  deleteMessage: (data) => serverApiCall(`/api/messages/${data.id}`, 'DELETE'),
  markMessageAsRead: (data) => serverApiCall(`/api/messages/${data.id}/read`, 'PUT'),
  
  // 系统设置
  getSettings: () => serverApiCall('/api/settings', 'GET'),
  updateSettings: (data) => serverApiCall('/api/settings', 'PUT', data),
  
  // 商城设置
  getShopSettings: () => serverApiCall('/api/shop/settings', 'GET'),
  updateShopSettings: (data) => serverApiCall('/api/shop/settings', 'PUT', data),
  updateShopSetting: (key, settingData) => {
    // 将单个设置转换为批量更新格式
    const settings = { [key]: settingData.value };
    return serverApiCall('/api/shop/settings', 'PUT', settings);
  },
  batchUpdateShopSettings: (data) => serverApiCall('/api/shop/settings', 'PUT', data),
  
  // 统计数据
  getStatistics: () => serverApiCall('/api/statistics', 'GET'),
  getOrderStatistics: () => serverApiCall('/api/statistics/orders', 'GET'),
  getTenantStatistics: () => serverApiCall('/api/statistics/tenants', 'GET'),
  getToolStatistics: () => serverApiCall('/api/statistics/tools', 'GET'),
  
  // 文件上传
  uploadFile: async (data) => {
    console.log('🔄 文件上传开始:', data)
    
    // 验证文件对象
    if (!data.file || !(data.file instanceof File)) {
      console.error('❌ 无效的文件对象:', data.file)
      throw new Error('无效的文件对象')
    }
    
    const formData = new FormData()
    formData.append('file', data.file)
    if (data.folder) formData.append('folder', data.folder)
    
    console.log('📦 FormData构建完成:', {
      file: data.file,
      fileName: data.file?.name,
      fileSize: data.file?.size,
      fileType: data.file?.type,
      formDataKeys: Array.from(formData.keys()),
      formDataEntries: Array.from(formData.entries()).map(([key, value]) => 
        key === 'file' ? [key, `File(${value.name}, ${value.size}bytes)`] : [key, value]
      )
    })
    
    // 🎯 修复：使用配置的apiClient并正确设置请求头
    try {
      console.log('📤 使用apiClient发送上传请求到:', buildApiUrl('/api/upload'))
      
      const response = await apiClient.post('/api/upload', formData, {
        timeout: API_CONFIG.timeout,
        headers: {
          // 不设置Content-Type，让浏览器自动设置multipart/form-data和boundary
        }
      })
      
      console.log('✅ 文件上传成功:', response.data)
      
      return {
        success: true,
        data: response.data.data || response.data,
        message: response.data.message
      }
    } catch (error) {
      console.error('❌ 文件上传失败:', error)
      console.error('❌ 错误详情:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      })
      throw error
    }
  },
  uploadImage: (data) => adminAPI.uploadFile(data), // 别名
  
  // AI功能（如果server端支持）
  generateCover: (data) => serverApiCall('/api/ai/generate-cover', 'POST', data),
  generateDescription: (data) => serverApiCall('/api/ai/generate-description', 'POST', data),
  generateOptionImage: (data) => serverApiCall('/api/ai/generate-option-image', 'POST', data),
  
  // 数据迁移管理
  getMigrationStatus: () => serverApiCall('/api/migration/status', 'GET'),
  startMigration: (data) => serverApiCall('/api/migration/start', 'POST', data),
  migrateSingleCollection: (data) => serverApiCall('/api/migration/collection', 'POST', data),
  rollbackMigration: (data) => serverApiCall('/api/migration/rollback', 'POST', data),
  
  // 数据备份管理
  getBackupStatus: () => serverApiCall('/api/backup/status', 'GET'),
  createBackup: (data) => serverApiCall('/api/backup/create', 'POST', data),
  getBackupList: () => serverApiCall('/api/backup/list', 'GET'),
  restoreBackup: (data) => serverApiCall('/api/backup/restore', 'POST', data),
  deleteBackup: (data) => serverApiCall(`/api/backup/${data.id}`, 'DELETE'),
  
  // URL转换管理
  getUrlConversionStatus: () => serverApiCall('/api/conversion/url/status', 'GET'),
  startUrlConversion: (data) => serverApiCall('/api/conversion/url/start', 'POST', data),
  previewUrlConversion: (data) => serverApiCall('/api/conversion/url/preview', 'POST', data),
  
  // 测试功能
  testConnection: () => serverApiCall('/health', 'GET'),
  
  // 通用HTTP方法
  get: (endpoint, params) => serverApiCall(endpoint, 'GET', params),
  post: (endpoint, data) => serverApiCall(endpoint, 'POST', data),
  put: (endpoint, data) => serverApiCall(endpoint, 'PUT', data),
  delete: (endpoint) => serverApiCall(endpoint, 'DELETE'),
  
  // AI任务管理
  // AI任务相关接口已移除
  
  // 历史记录管理 (替换AI任务管理)
  getHistory: (data) => serverApiCall('/api/history', 'GET', data),
  getHistoryStats: (data) => serverApiCall('/api/history/stats', 'GET', data),
  deleteHistory: (id) => serverApiCall(`/api/history/${id}`, 'DELETE'),
  batchDeleteHistory: (ids) => serverApiCall('/api/history', 'DELETE', { ids }),
  updateHistoryVisibility: (id, isVisible) => serverApiCall(`/api/history/${id}/visibility`, 'PUT', { isVisible }),
  
  // 域名管理
  getDomainWhitelist: (data) => serverApiCall('/api/domain-whitelist', 'GET', data),
  addDomainWhitelist: (data) => serverApiCall('/api/domain-whitelist', 'POST', data),
  updateDomainWhitelist: (data) => serverApiCall(`/api/domain-whitelist/${data.id}`, 'PUT', data),
  deleteDomainWhitelist: (id) => serverApiCall(`/api/domain-whitelist/${id}`, 'DELETE'),
  batchUpdateDomainStatus: (data) => serverApiCall('/api/domain-whitelist/batch/status', 'PUT', data),
  getDomainStats: () => serverApiCall('/api/domain-whitelist/stats', 'GET'),
  verifyDomain: (domain) => serverApiCall(`/api/domain-whitelist/verify/${domain}`, 'GET'),
  
  // Coze工作流相关
  runCozeWorkflow: (data) => serverApiCall('/api/ai-tasks/workflow', 'POST', data),
  runCozeWorkflowSync: (data) => serverApiCall('/api/ai-tasks/workflow/sync', 'POST', data, { timeout: 300000 }), // 5分钟超时
  getCozeWorkflowResult: (taskId) => serverApiCall(`/api/ai-tasks/${taskId}`, 'GET'),
  
  // 兼容性API
  checkAuthStatus: async () => {
    const token = getAuthToken()
    return { success: true, isAuthenticated: !!token }
  },
  
  // 通用GET方法
  get: (endpoint, params) => serverApiCall(endpoint, 'GET', params),
  post: (endpoint, data) => serverApiCall(endpoint, 'POST', data),
  put: (endpoint, data) => serverApiCall(endpoint, 'PUT', data),
  delete: (endpoint) => serverApiCall(endpoint, 'DELETE')
}

// 添加租户管理API
export const tenantManagementAPI = {
  // 获取租户数据统计
  async getTenantStats(tenantId) {
    const response = await apiClient.get(`/api/tenant-management/stats/${tenantId}`)
    return response.data
  },
  
  // 完整删除租户
  async deleteTenantComplete(tenantId) {
    const response = await apiClient.delete(`/api/tenant-management/${tenantId}/complete`)
    return response.data
  },
  
  // 导出租户数据
  async exportTenantData(tenantId) {
    const response = await apiClient.get(`/api/tenant-management/${tenantId}/export`, {
      responseType: 'blob'
    })
    return response
  },
  
  // 导入租户数据
  async importTenantData(importData, options = {}) {
    // 🔥 适配新的智能导入API格式
    const response = await apiClient.post(`/api/tenant-management/import`, {
      targetTenantId: options.targetTenantId || '6', // 默认导入到租户6
      data: JSON.stringify(importData) // 将数据序列化为字符串
    })
    return response.data
  }
}

// WebSocket管理器
class WebSocketManager {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    this.eventHandlers = new Map()
  }

  connect() {
    const token = getAuthToken()
    if (!token) {
      console.warn('🔌 WebSocket连接失败: 缺少认证token')
      return
    }

    if (this.socket) {
      this.disconnect()
    }

    console.log('🔌 正在连接WebSocket...')
    
    // WebSocket连接地址
        const wsUrl = window.APP_CONFIG?.WS_URL || window.APP_CONFIG?.API_BASE_URL || API_CONFIG.serverUrl
    
    this.socket = io(wsUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay
    })

    this.socket.on('connect', () => {
      console.log('✅ WebSocket连接成功')
      this.isConnected = true
      this.reconnectAttempts = 0
      this.emit('connected', { timestamp: new Date().toISOString() })
    })

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket连接断开:', reason)
      this.isConnected = false
      this.emit('disconnected', { reason })
    })

    this.socket.on('connect_error', (error) => {
      console.error('🔌 WebSocket连接错误:', error.message)
      this.reconnectAttempts++
      this.emit('error', { error: error.message, attempts: this.reconnectAttempts })
    })

    // 任务相关事件
    this.socket.on('task:update', (data) => {
      console.log('📊 [前端] 收到任务状态更新:', data)
      this.emit('task:update', data)
    })

    this.socket.on('task:completed', (data) => {
      console.log('🎉 [前端] 收到任务完成通知:', data)
      this.emit('task:completed', data)
    })

    this.socket.on('task:failed', (data) => {
      console.log('💥 [前端] 收到任务失败通知:', data)
      this.emit('task:failed', data)
    })
    
    // 监听所有事件（调试用）
    this.socket.onAny((eventName, ...args) => {
      console.log(`🔔 [前端] WebSocket事件: ${eventName}`, args)
    })

    // 系统消息
    this.socket.on('system:message', (data) => {
      console.log('📢 系统消息:', data)
      this.emit('system:message', data)
    })

    return this.socket
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      console.log('🔌 WebSocket连接已断开')
    }
  }

  // 事件发射器
  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event).add(handler)
  }

  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).delete(handler)
    }
  }

  emit(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach(handler => {
        try {
          handler(data)
        } catch (error) {
          console.error(`WebSocket事件处理器错误 [${event}]:`, error)
        }
      })
    }
  }

  // 订阅任务状态更新
  subscribeToTasks(taskIds) {
    if (this.socket && this.isConnected) {
      this.socket.emit('subscribe:tasks', { taskIds })
      console.log(`📺 已订阅 ${taskIds.length} 个任务的状态更新`)
    }
  }

  // 取消订阅任务
  unsubscribeFromTasks(taskIds) {
    if (this.socket && this.isConnected) {
      this.socket.emit('unsubscribe:tasks', { taskIds })
      console.log(`📺 已取消订阅 ${taskIds.length} 个任务`)
    }
  }

  // 获取任务状态
  getTaskStatus(taskId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('get:task:status', { taskId })
    }
  }

  // 取消任务
  cancelTask(taskId) {
    if (this.socket && this.isConnected) {
      this.socket.emit('cancel:task', { taskId })
    }
  }
}

// 创建全局WebSocket管理器实例
export const wsManager = new WebSocketManager()

// 兼容性适配器 - 将旧的apiCall调用转换为adminAPI调用
export async function apiCall(action, data = {}) {
  console.log(`🔄 [兼容性] apiCall转换: ${action}`, data)
  
  // 方法映射表
  const methodMap = {
    // 工具管理
    'getTools': () => adminAPI.getTools(data),
    'createTool': () => adminAPI.createTool(data),
    'updateTool': () => adminAPI.updateTool(data),
    'deleteTool': () => adminAPI.deleteTool(data),
    
    // 分类管理
    'getCategories': () => adminAPI.getCategories(),
    'updateCategories': () => adminAPI.updateCategories(data),
    'deleteCategory': () => adminAPI.deleteCategory(data),
    
    // 设置管理
    'getSettings': () => adminAPI.getSettings(),
    'updateSettings': () => adminAPI.updateSettings(data),
    'getShopSettings': () => adminAPI.getShopSettings(),
    'getShopSettingByKey': () => adminAPI.getShopSettingByKey(data.key || data),
    'updateShopSetting': () => adminAPI.updateShopSetting(data.key, data),
    'batchUpdateShopSettings': () => adminAPI.batchUpdateShopSettings(data),
    
    // 用户管理
    'getUsers': () => adminAPI.getUsers(data),
    'updateUser': () => adminAPI.updateUser(data),
    'updateUserPoints': () => adminAPI.updateUserPoints(data),
    
    // 租户管理
    'getTenants': () => adminAPI.getTenants(data),
    'createTenant': () => adminAPI.createTenant(data),
    'updateTenant': () => adminAPI.updateTenant(data),
    'deleteTenant': () => adminAPI.deleteTenant(data),
    
    // 订单管理
    'getOrders': () => adminAPI.getOrders(data),
    'updateOrder': () => adminAPI.updateOrder(data),
    'deleteOrder': () => adminAPI.deleteOrder(data),
    'getToolOrders': () => adminAPI.getToolOrders(data),
    'getToolOrderStatistics': () => adminAPI.getToolOrderStatistics(),
    
    // 消息管理
    'getMessages': () => adminAPI.getMessages(data),
    'sendMessage': () => adminAPI.sendMessage(data),
    'deleteMessage': () => adminAPI.deleteMessage(data),
    'markMessageAsRead': () => adminAPI.markMessageAsRead(data),
    
    // 统计数据
    'getStatistics': () => adminAPI.getStatistics(),
    'getOrderStatistics': () => adminAPI.getOrderStatistics(),
    'getTenantStatistics': () => adminAPI.getTenantStatistics(),
    'getToolStatistics': () => adminAPI.getToolStatistics(),
    
    // 文件上传
    'uploadFile': () => adminAPI.uploadFile(data),
    'uploadImage': () => adminAPI.uploadImage(data),
    
    // AI功能
    'generateCover': () => adminAPI.generateCover(data),
    'generateDescription': () => adminAPI.generateDescription(data),
    'generateOptionImage': () => adminAPI.generateOptionImage(data),
    'runCozeWorkflow': () => adminAPI.runCozeWorkflow(data),
    'getCozeWorkflowResult': () => adminAPI.getCozeWorkflowResult(data.taskId || data),
    
    // 数据迁移
    'getMigrationStatus': () => adminAPI.getMigrationStatus(),
    'startMigration': () => adminAPI.startMigration(data),
    'migrateSingleCollection': () => adminAPI.migrateSingleCollection(data),
    'rollbackMigration': () => adminAPI.rollbackMigration(data),
    
    // 数据备份
    'getBackupStatus': () => adminAPI.getBackupStatus(),
    'createBackup': () => adminAPI.createBackup(data),
    'getBackupList': () => adminAPI.getBackupList(),
    'restoreBackup': () => adminAPI.restoreBackup(data),
    'deleteBackup': () => adminAPI.deleteBackup(data),
    
    // URL转换
    'getUrlConversionStatus': () => adminAPI.getUrlConversionStatus(),
    'startUrlConversion': () => adminAPI.startUrlConversion(data),
    'previewUrlConversion': () => adminAPI.previewUrlConversion(data),
    
    // 测试功能
    'testConnection': () => adminAPI.testConnection(),
    
    // AI任务管理
    // AI任务功能已移除
    'createWorkflowTask': () => adminAPI.createWorkflowTask(data),
    'createChatTask': () => adminAPI.createChatTask(data),
    'getTaskStatus': () => adminAPI.getCozeWorkflowResult(data.taskId || data),
    'runCozeWorkflowSync': () => adminAPI.runCozeWorkflowSync(data),
    'testAuthFlow': () => adminAPI.testAuthFlow(),
    
    // 历史记录管理
    'getHistory': () => adminAPI.getHistory(data),
    'getHistoryStats': () => adminAPI.getHistoryStats(data),
    'deleteHistory': () => adminAPI.deleteHistory(data.id),
    'batchDeleteHistory': () => adminAPI.batchDeleteHistory(data.ids),
    'updateHistoryVisibility': () => adminAPI.updateHistoryVisibility(data.id, data.isVisible),
    
    // 通用GET方法
    'get': () => adminAPI.get(data.endpoint, data.params),
    'post': () => adminAPI.post(data.endpoint, data.data),
    'put': () => adminAPI.put(data.endpoint, data.data),
    'delete': () => adminAPI.delete(data.endpoint)
  }
  
  if (methodMap[action]) {
    const result = await methodMap[action]()
    console.log(`✅ [兼容性] ${action} 转换完成:`, result)
    return result
  } else {
    console.error(`❌ [兼容性] 未知的API方法: ${action}`)
    return {
      success: false,
      message: `未知的API方法: ${action}`
    }
  }
}

// 导出所有必要的函数
export { serverApiCall, getApiConfig }
export default adminAPI

console.log('✅ Super端API初始化完成 - 已完全移除CloudBase，专用于Server端对接')
